import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, CardContent } from '../../components/ui/card';
import BackButton from '../../components/ui/BackNavigationButton';
import NavigationButton from '../../components/ui/NavigationButton';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import { pages } from '../../components/utils/pages-links';
import TutorialOverlay from '../../components/ui/TutorialOverlay';
import { useAlert } from '../../context/alert/AlertProvider';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from "lucide-react";
import Tooltip from "../../components/ui/ToolTip";
import ScenarioIndicator from "../../components/forms/scenario/ScenarioIndicator";
import useAutoSave from "../../hooks/useAutoSave";

const LaborLegislationPage = () => {
    const [restTime, setRestTime] = useState('11');
    const [consecutiveDays, setConsecutiveDays] = useState('5');
    const [showTutorial, setShowTutorial] = useState(false);
    const [tutorialStep, setTutorialStep] = useState(0);

    const formRef = useRef(null);
    const nextButtonRef = useRef(null);

    const navigate = useNavigate();
    const location = useLocation();
    const { showAlert } = useAlert();
    const { t } = useTranslation();
    const isExampleMode = location.state?.isExample;

    useAutoSave("legislation", {restTime,consecutiveDays})

    const tutorialSteps = [
        {
            title: t('tutorial.legislation.title') || "Labor Legislation Settings",
            description: t('tutorial.legislation.description') || "Configure important labor legislation parameters that affect your schedule.",
            position:"center"
        },
        {
            title: t('tutorial.legislation.parameters.title') || "Parameters",
            description: t('tutorial.legislation.parameters.description') || "Set the minimum rest time between shifts and maximum consecutive working days.",
            position:"top-right",
            elementRef: formRef
        },
        {
            title: t('tutorial.legislation.next.title') || "Ready to Continue",
            description: t('tutorial.legislation.next.description') || "Click Next to continue to the next step.",
            position:"next-button",
            elementRef: nextButtonRef
        }
    ];

    useEffect(() => {
        if (isExampleMode) {
            setShowTutorial(true);
        }

        const loadData = () => {
            const savedLegislation = JSON.parse(sessionStorage.getItem('legislation') || '{}');
            if (savedLegislation.restTime){
                setRestTime(savedLegislation.restTime);
            }
            if (savedLegislation.consecutiveDays) {
                setConsecutiveDays(savedLegislation.consecutiveDays);
            }
        };

        loadData();
    }, [isExampleMode]);

    const handleInputChange = (setter) => (e) => {
        const value = e.target.value.replace(/[^0-9]/g, '');
        setter(value)
    }

    const validateAndSave = () => {
        if (!restTime || !consecutiveDays) {
            showAlert({
                title: t('alert.validation.error'),
                description: t('alert.error.fillAllFields'),
                variant: 'destructive'
            });
            return false;
        }

        const parsedRestTime = parseInt(restTime, 10);
        const parsedConsecutiveDays = parseInt(consecutiveDays, 10);

        if (parsedRestTime < 8 || parsedRestTime > 24) {
            showAlert({
                title: t('alert.validation.error'),
                description: t('alert.error.restTimeRange'),
                variant: 'destructive'
            });
            return false;
        }

        if (parsedConsecutiveDays < 1 || parsedConsecutiveDays > 7) {
            showAlert({
                title: t('alert.validation.error'),
                description: t('alert.error.consecutiveDaysRange'),
                variant: 'destructive'
            });
            return false;
        }

        return true;
    };

    const handleNext = () => {
        if (validateAndSave()){
            navigate('/form/absences', {
                state: { isExample: isExampleMode}
            });
        }
    };

    const handleBack = () => {
        navigate('/form/schedule', {
            state: { isExample: isExampleMode }
        });
    };

    const handleNextTutorialStep = () => {
        if (tutorialStep < tutorialSteps.length - 1) {
            setTutorialStep(prev => prev + 1);
        } else {
            setShowTutorial(false);
        }
    };

    return (
        <div className="-mt-16">
            <Breadcrumbs pages={pages}/>
            <div className="p-6 bg-gray-100 min-h-screen">
                <div className="max-w-4xl mx-auto space-y-8">
                    <div className="flex justify-between items-center">
                        <BackButton onClick={handleBack}/>
                        <NavigationButton ref={nextButtonRef} onClick={handleNext} />
                    </div>

                    <ScenarioIndicator />

                    <Card className="mb-6">
                        <CardContent className="p-6">
                            <div className="flex items-center gap-2 mb-6">
                                <h1 className="text-2xl font-bold">{t("form.legislation.title") || "Labor Legislation"}</h1>
                                <div className="flex items-center h-8">
                                    <Tooltip content={t("tooltip.legislation") || "Configure labor legislation parameters that affect your schedule."}/>
                                </div>
                            </div>

                            <div ref={formRef} className="space-y-6">
                                <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                                    <div className="flex items-start gap-3">
                                        <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5" />
                                        <div>
                                            <h3 className="text-sm font-medium text-amber-800">{t("form.legislation.notice") || "Important Notice"}</h3>
                                            <p className="text-sm text-amber-700 mt-1">
                                                {t("form.legislation.notice.description") || "These settings affect how the scheduling algorithm respects labor laws. Ensure values comply with your local regulations."}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            {t("form.legislation.restTime") || "Minimum Rest Time Between Shifts (hours)"}
                                            <span className="text-red-500 ml-1">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            value={restTime}
                                            onChange={handleInputChange(setRestTime)}
                                            min={8}
                                            max={24}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            disabled={isExampleMode}
                                        />
                                        <p className="mt-1 text-sm text-gray-500">
                                            {t("form.legislation.restTime.description") || "Standard is 11 hours. Legal minimum is typically 8-12 hours depending on your region"}
                                        </p>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            {t("form.legislation.consecutiveDays") || "Maximum Consecutive Working Days"}
                                            <span className="text-red-500 ml-1">*</span>
                                        </label>
                                        <input 
                                            type="number"
                                            value={consecutiveDays}
                                            onChange={handleInputChange(setConsecutiveDays)}
                                            min={1}
                                            max={7}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                        <p className="mt-1 text-sm text-gray-500">
                                            {t("form.legislation.consecutiveDays.description") || "Standard is 5-6 days. After this many consecutive work days, a rest day will be scheduled."}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            <TutorialOverlay 
                isVisible={showTutorial}
                onClose={() => setShowTutorial(false)}
                title={tutorialSteps[tutorialStep]?.title}
                description={tutorialSteps[tutorialStep]?.description}
                position={tutorialSteps[tutorialStep]?.position}
                targetRef={tutorialSteps[tutorialStep]?.elementRef}
                onNext={handleNextTutorialStep}
                isLastStep={tutorialStep === tutorialSteps.length - 1}
            />


        </div>

        

    );
}

export default LaborLegislationPage;